import { useInviteToken } from '@/components/auth/useInviteToken';
import { changeOrderOfferTranslations } from '@/helpers/changeOrderOfferTranslations';
import { formatDate } from '@/helpers/dates';
import { useTranslation } from '@/hooks/useTranslation';
import {
  useAcceptChangeOrderMutation,
  useDeclineChangeOrderMutation,
  useGetChangeOrderReviewQuery,
} from '@/services/changeOrderOffer';
import { PalmettoButton, PalmettoIcon, PalmettoModal, PalmettoTextarea } from '@palmetto/dsm-components';
import { Themes } from '@palmetto/dsm-tokens';
import { Spinner } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { ChangeOrderStatus, FinanceType, Locale, TERMINAL_CHANGE_ORDER_STATUSES } from 'types';

const TERMINAL_STATUSES = new Set<ChangeOrderStatus>(TERMINAL_CHANGE_ORDER_STATUSES);

// A lease and a PPA are different agreements — the heading must match which one this customer
// actually has. Defaults to the PPA wording only if financeType is unexpectedly missing.
const HEADING_TRANSLATION_KEYS: Record<FinanceType, string> = {
  [FinanceType.ppa]: 'headingPpa',
  [FinanceType.lease]: 'headingLease',
};

// A change order's sequence (1-based) drives "First/Second/Third Amendment" — a contract's Nth
// amendment must say "Nth", not always "First". Spanish forms agree with the feminine "Enmienda".
const AMENDMENT_ORDINAL_WORDS: Record<Locale, string[]> = {
  [Locale.en]: ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'],
  [Locale.es]: [
    'Primera',
    'Segunda',
    'Tercera',
    'Cuarta',
    'Quinta',
    'Sexta',
    'Séptima',
    'Octava',
    'Novena',
    'Décima',
  ],
};

const englishNumericOrdinalSuffix = (n: number): string => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const getAmendmentOrdinalWord = (locale: Locale, sequence: number): string => {
  const word = AMENDMENT_ORDINAL_WORDS[locale]?.[sequence - 1];
  if (word) return word;
  return locale === Locale.es ? `${sequence}.ª` : `${sequence}${englishNumericOrdinalSuffix(sequence)}`;
};

const BrandHeader = () => (
  <div className="flex justify-center pt-6">
    <img src="/palmetto-brand-logo.svg" alt="Palmetto LightReach" style={{ height: '44px', width: 'auto' }} />
  </div>
);

export const ChangeOrderOffer = () => {
  const { id, changeOrderId } = useParams<{ id: string; changeOrderId: string }>();
  const { inviteToken } = useInviteToken();
  const { translate, locale } = useTranslation({ useAccountPreference: true });
  const t = (key: string) => translate(key, changeOrderOfferTranslations);

  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const { control, handleSubmit } = useForm<{ reason: string }>({ defaultValues: { reason: '' } });

  const { data, isLoading, isError } = useGetChangeOrderReviewQuery(
    { accountId: id!, changeOrderId: changeOrderId!, inviteToken },
    { skip: !id || !changeOrderId },
  );

  const [acceptChangeOrder, { isLoading: isAccepting, isError: isAcceptError, isSuccess: isAccepted }] =
    useAcceptChangeOrderMutation();
  const [declineChangeOrder, { isLoading: isDeclining, isError: isDeclineError }] = useDeclineChangeOrderMutation();

  const handleAccept = useCallback(() => {
    acceptChangeOrder({ accountId: id!, changeOrderId: changeOrderId!, inviteToken });
  }, [acceptChangeOrder, id, changeOrderId, inviteToken]);

  const openDeclineModal = useCallback(() => setIsDeclineModalOpen(true), []);
  const closeDeclineModal = useCallback(() => setIsDeclineModalOpen(false), []);

  const onDeclineSubmit = handleSubmit(async ({ reason }) => {
    try {
      await declineChangeOrder({ accountId: id!, changeOrderId: changeOrderId!, inviteToken, reason }).unwrap();
      setIsDeclineModalOpen(false);
    } catch {
      // isDeclineError renders the footer error; keep the modal open so the signer can retry.
    }
  });

  const currentSigner = data?.signers[0];
  const firstName = currentSigner?.name.split(' ')[0] ?? '';
  const pendingCoSigners = (data?.signers.slice(1) ?? []).filter((s) => !s.hasAccepted);
  const status = data?.status;
  const hasAcceptedAsSigner = isAccepted || !!currentSigner?.hasAccepted;
  const isDeclined = status === ChangeOrderStatus.declined;
  const isVoided = status === ChangeOrderStatus.voided || status === ChangeOrderStatus.cancelled;
  const isExecuted = status === ChangeOrderStatus.executed;
  const isExpired = status === ChangeOrderStatus.expired;
  // Belt-and-suspenders: offer links are only generated once a CO leaves this status, so a signer
  // should never actually land here in this state. Guard anyway rather than showing accept/decline.
  const isAwaitingDisclosure = status === ChangeOrderStatus.awaitingDisclosure;
  const isTerminal = status ? TERMINAL_STATUSES.has(status) : false;

  if (isLoading) {
    return (
      <div data-theme={Themes.core}>
        <div className="flex min-h-screen items-center justify-center bg-canvas-default">
          <Spinner />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div data-theme={Themes.core}>
        <div className="flex min-h-screen items-center justify-center bg-canvas-default">
          <p className="body-300 text-text-primary">{t('errorLoading')}</p>
        </div>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div data-theme={Themes.core}>
        <Helmet>
          <title>{t('helmetTitle')}</title>
        </Helmet>
        <div className="flex min-h-screen flex-col bg-canvas-default">
          <BrandHeader />
          <div className="flex flex-1 items-center justify-center px-8">
            <h1 className="text-center heading-700 text-text-primary">{t('expired')}</h1>
          </div>
        </div>
      </div>
    );
  }

  if (isVoided) {
    return (
      <div data-theme={Themes.core}>
        <Helmet>
          <title>{t('helmetTitle')}</title>
        </Helmet>
        <div className="flex min-h-screen flex-col bg-canvas-default">
          <BrandHeader />
          <div className="flex flex-1 items-center justify-center px-8">
            <h1 className="text-center heading-700 text-text-primary">{t('voided')}</h1>
          </div>
        </div>
      </div>
    );
  }

  if (isAwaitingDisclosure) {
    return (
      <div data-theme={Themes.core}>
        <Helmet>
          <title>{t('helmetTitle')}</title>
        </Helmet>
        <div className="flex min-h-screen flex-col bg-canvas-default">
          <BrandHeader />
          <div className="flex flex-1 items-center justify-center px-8">
            <h1 className="text-center heading-700 text-text-primary">{t('awaitingDisclosure')}</h1>
          </div>
        </div>
      </div>
    );
  }

  if (isDeclined) {
    return (
      <div data-theme={Themes.core}>
        <Helmet>
          <title>{t('helmetTitle')}</title>
        </Helmet>
        <div className="flex min-h-screen flex-col bg-canvas-default">
          <BrandHeader />
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
            <h1 className="heading-800 text-text-primary">{t('declinedHeading')}</h1>
            <p className="body-600 text-text-primary" style={{ maxWidth: '44rem' }}>
              {t('declinedBody')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isExecuted) {
    return (
      <div data-theme={Themes.core}>
        <Helmet>
          <title>{t('helmetTitle')}</title>
        </Helmet>
        <div className="flex min-h-screen flex-col bg-canvas-default">
          <BrandHeader />
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
            <h1 className="heading-800 text-text-primary">{t('executedHeading')}</h1>
            <p className="body-600 text-text-primary" style={{ maxWidth: '44rem' }}>
              {t('executedBody')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (hasAcceptedAsSigner && !isTerminal) {
    return (
      <div data-theme={Themes.core}>
        <Helmet>
          <title>{t('helmetTitle')}</title>
        </Helmet>
        <div className="flex min-h-screen flex-col bg-canvas-default">
          <BrandHeader />
          <div className="flex flex-1 items-center justify-center px-8">
            <div className="flex flex-col gap-8 text-center" style={{ maxWidth: '44rem' }}>
              <div className="flex items-center justify-center gap-12">
                <PalmettoIcon name="celebrate" size="4xl" />
                <h1 className="heading-800 text-text-primary">
                  {t('confirmationHeading')}
                  {firstName ? `, ${firstName}` : ''}!
                </h1>
              </div>
              <p className="body-600 text-text-primary">
                {pendingCoSigners.length > 0 ? (
                  <>
                    {t('confirmationBodyAdditionalSignersBefore')}{' '}
                    {pendingCoSigners.map((signer, i) => (
                      <span key={signer.email}>
                        <strong>{signer.name}</strong>
                        {i < pendingCoSigners.length - 1 ? ', ' : ' '}
                      </span>
                    ))}
                    {t('confirmationBodyAdditionalSignersAfter')}
                  </>
                ) : (
                  <>
                    {t('confirmationBodySolo')} <strong>{currentSigner?.email}</strong>.
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-center gap-2 pb-12 text-center body-400 text-text-tertiary">
            <p>{t('confirmationQuestion')}</p>
            <p>
              {t('confirmationContactBefore')} <strong>{t('contactEmail')}</strong> {t('confirmationOr')}{' '}
              <strong>{t('contactPhone')}</strong>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-theme={Themes.core}>
      <Helmet>
        <title>{t('helmetTitle')}</title>
      </Helmet>

      <div className="flex min-h-screen flex-col bg-canvas-default" style={{ paddingBottom: '150px' }}>
        <div className="gap-7 flex flex-col items-center px-4 pt-6 pb-12">
          <img
            className="mb-24"
            src="/palmetto-brand-logo.svg"
            alt="Palmetto LightReach"
            style={{ height: '50px', width: 'auto' }}
          />
          <div className="flex w-full max-w-2xl flex-col items-center gap-4 text-center">
            <h1 className="heading-700 text-text-primary">
              {getAmendmentOrdinalWord(locale, data.sequence)}{' '}
              {t(HEADING_TRANSLATION_KEYS[data.financeType ?? FinanceType.ppa])}
            </h1>
            {data.epcName && (
              <p className="body-400 text-text-secondary">
                {t('proposedBy')} <strong>{data.epcName}</strong>
              </p>
            )}
          </div>
        </div>

        <div className="flex w-full justify-center px-4">
          <div className="flex w-full flex-col gap-10" style={{ maxWidth: '64rem' }}>
            <div className="flex flex-col gap-4">
              <h2 className="heading-400 text-text-primary">{t('changesTitle')}</h2>
              <div className="overflow-x-auto">
                <table className="w-full body-300 text-text-primary">
                  <thead>
                    <tr className="text-text-secondary">
                      <th className="py-2 pr-4 text-left">{t('fieldColumn')}</th>
                      <th className="py-2 pr-4 text-left">{t('previousColumn')}</th>
                      <th className="py-2 text-left">{t('updatedColumn')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.changeSummary ?? []).map((row) => (
                      <tr key={row.label} className="border-t border-canvas-inverted">
                        <td className="py-2 pr-4 font-semibold">{row.label}</td>
                        <td className="py-2 pr-4 text-text-secondary">
                          <span style={{ textDecoration: 'line-through' }}>{row.previousValue}</span>
                        </td>
                        <td className="py-2">
                          <strong>{row.newValue}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(data.changeSummaryUnchanged ?? []).length > 0 && (
                <div className="flex flex-col gap-4">
                  <h2 className="heading-400 text-text-primary">{t('unchangedTitle')}</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full body-300 text-text-primary">
                      <thead>
                        <tr className="text-text-secondary">
                          <th className="py-2 pr-4 text-left">{t('fieldColumn')}</th>
                          <th className="py-2 text-left">{t('valueColumn')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.changeSummaryUnchanged ?? []).map((row) => (
                          <tr key={row.label} className="border-t border-canvas-inverted">
                            <td className="py-2 pr-4 font-semibold">{row.label}</td>
                            <td className="py-2">{row.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <p className="body-200 text-text-secondary">{t('allTermsRemain')}</p>
              {data.originalContractUrl && (
                <a
                  className="text-text-link body-300 underline"
                  href={data.originalContractUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('originalContract')}
                </a>
              )}
            </div>

            <div className="w-full overflow-hidden rounded-36 border-2 border-canvas-inverted">
              <div className="flex items-center justify-center rounded-t-36 bg-canvas-inverted px-4 py-4">
                <span className="heading-200 text-text-on-inverted-primary">{t('fullContract')}</span>
              </div>
              {data.contractHtml ? (
                <iframe
                  srcDoc={data.contractHtml}
                  title={t('fullContract')}
                  sandbox="allow-same-origin"
                  onLoad={(e) => {
                    const iframe = e.currentTarget;
                    if (iframe.contentDocument?.documentElement) {
                      iframe.style.height = `${iframe.contentDocument.documentElement.scrollHeight + 50}px`;
                    }
                  }}
                  style={{ width: '100%', height: 0, border: 'none', overflow: 'hidden' }}
                />
              ) : (
                <div className="min-h-96 bg-canvas-default p-8" />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="fixed z-50 bg-canvas-bolder" style={{ bottom: 0, left: 0, right: 0 }}>
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-2 px-8 py-10">
          <p className="text-center body-200 text-text-secondary">{t('acceptDisclaimer')}</p>
          <div className="flex items-center gap-4">
            <PalmettoButton
              type="button"
              emphasis="secondary"
              size="large"
              layout="hug"
              disabled={isAccepting || isDeclining}
              onClick={openDeclineModal}
            >
              {t('decline')}
            </PalmettoButton>
            <PalmettoButton
              type="button"
              emphasis="primary"
              size="large"
              layout="hug"
              trailingIcon={<PalmettoIcon name="arrowRight" size="sm" />}
              loading={isAccepting}
              disabled={isAccepting || isDeclining}
              onClick={handleAccept}
            >
              {t('accept')}
            </PalmettoButton>
          </div>
          {data.expiresAt && (
            <p className="text-center body-200 text-text-secondary">
              {t('contractExpires')} {formatDate(data.expiresAt, DateTime.DATE_FULL)}
            </p>
          )}
          {isAcceptError && <p className="text-center body-200 text-text-error">{t('acceptError')}</p>}
          {isDeclineError && <p className="text-center body-200 text-text-error">{t('declineError')}</p>}
        </div>
      </div>

      <PalmettoModal open={isDeclineModalOpen} onClose={closeDeclineModal} invertedBackground={false}>
        <div className="flex flex-col gap-4">
          <h2 className="heading-400 text-text-primary">{t('declineModalHeading')}</h2>
          <p className="body-300 text-text-secondary">{t('declineModalBody')}</p>
          <Controller
            name="reason"
            control={control}
            rules={{ required: t('declineReasonRequired') }}
            render={({ field, fieldState }) => (
              <PalmettoTextarea
                id="change-order-decline-reason"
                label={t('declineReasonLabel')}
                placeholder={t('declineReasonPlaceholder')}
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
                onBlur={field.onBlur}
                rows={4}
                validationMessage={
                  fieldState.error ? { type: 'error', text: fieldState.error.message ?? '' } : undefined
                }
              />
            )}
          />
          <div className="flex justify-end gap-4">
            <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={closeDeclineModal}>
              {t('cancel')}
            </PalmettoButton>
            <PalmettoButton
              type="button"
              emphasis="primary"
              layout="hug"
              loading={isDeclining}
              disabled={isDeclining}
              onClick={onDeclineSubmit}
            >
              {t('declineSubmit')}
            </PalmettoButton>
          </div>
        </div>
      </PalmettoModal>
    </div>
  );
};
